import { Fragment, useEffect, useState, type FC } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { toast } from '@wartek-id/toast-v2'
import { Radio, RadioGroup } from '@wartek-id/radio'
import { Text } from '@wartek-id/text-v1'
import { Badge } from '@wartek-id/badge'

import {
  API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS,
  API_GET_LIST_PENILAIAN_TAHUNAN_DINAS,
  API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS,
  useApiPatchOverridePenilaianTahunanDinas,
} from 'api/kinerja/kepala-sekolah/dinas/penilaian-tahunan'
import {
  API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS_TAB_GURU,
  API_GET_LIST_PENILAIAN_TAHUNAN_DINAS_TAB_GURU,
  API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS_TAB_GURU,
  useApiPatchOverridePenilaianTahunanDinasTabGuru,
} from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian-tahunan'

import type { TabFilterRoles } from 'types/kinerja-ks'
import type { TPenilaianTahunan } from 'types/kinerja-dinas-penilaian'

type TProps = {
  year: number
  isOpen?: boolean
  onClose?: () => void
  data: TPenilaianTahunan | null
  activeTabName: TabFilterRoles
}

const options = [
  'Sangat Baik',
  'Baik',
  'Butuh Perbaikan',
  'Kurang',
  'Sangat Kurang',
]

const DialogPredikatKinerjaTahunanUpdate: FC<TProps> = ({
  year,
  isOpen,
  onClose,
  data,
  activeTabName = 'KS',
}) => {
  const queryClient = useQueryClient()

  const [selected, setSelected] = useState<string>(
    data?.predikatKinerjaRekomendasi
  )

  const { mutate: mutateKs, isLoading: isLoadingKs } =
    useApiPatchOverridePenilaianTahunanDinas({ year })

  const { mutate: mutateGuru, isLoading: isLoadingGuru } =
    useApiPatchOverridePenilaianTahunanDinasTabGuru({ year })

  const isMutating = isLoadingKs || isLoadingGuru

  const onSubmit = () => {
    const payload = {
      id: data?.id,
      ratingPredikatKinerja: selected,
    }

    const opts = {
      onError: () => {
        // Show error toast
        toast(
          `Gagal ubah Predikat Kinerja Pegawai (Tahunan) ${data?.name}. Coba lagi.`
        )
      },
      onSuccess: () => {
        // Show toast
        toast.success(
          `Berhasil ubah Rekomendasi Predikat Kinerja (Tahunan)<br />${data?.name}!`
        )

        // Close
        onClose()
      },
    }

    // Mutate
    if (activeTabName === 'GURU')
      mutateGuru(payload, {
        ...opts,
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS_TAB_GURU],
          })

          queryClient.invalidateQueries({
            queryKey: [API_GET_LIST_PENILAIAN_TAHUNAN_DINAS_TAB_GURU],
          })

          queryClient.invalidateQueries({
            queryKey: [API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS_TAB_GURU],
          })

          opts.onSuccess()
        },
      })
    else
      mutateKs(payload, {
        ...opts,
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [API_GET_COUNT_PENILAIAN_TAHUNAN_DINAS],
          })

          queryClient.invalidateQueries({
            queryKey: [API_GET_LIST_PENILAIAN_TAHUNAN_DINAS],
          })

          queryClient.invalidateQueries({
            queryKey: [API_GET_SUMMARY_PENILAIAN_TAHUNAN_DINAS],
          })

          opts.onSuccess()
        },
      })
  }

  useEffect(() => {
    setSelected(data?.predikatKinerjaDinas)

    return () => {
      setSelected(null)
    }
  }, [data?.predikatKinerjaDinas])

  return (
    <>
      <Dialog
        data-module="pengelolaan-kinerja"
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        type="modal"
        autoFocus={false}
        closeOnOverlayClick={false}
      >
        <DialogOverlay />
        <DialogContent>
          <DialogHeader title="Ubah Predikat Kinerja Pegawai (Tahunan)" />
          <div className="p-6 space-y-6">
            <div className="space-y-1">
              <Text variant="body" className="!text-gray-90">
                Nama Pegawai:
              </Text>
              <Text variant="heading-sm">{data?.name}</Text>
            </div>

            <RadioGroup
              name="select-user"
              onChange={setSelected}
              className="flex flex-col gap-4"
              value={selected}
            >
              {options.map((option, idx) => (
                <div
                  key={`${selected}-${idx}`}
                  className="!flex border border-subdued rounded p-3 cursor-pointer"
                  onClick={() => setSelected(option)}
                >
                  <div className="w-full flex flex-row items-center justify-between">
                    <Radio value={option}>
                      <Text>{option}</Text>
                    </Radio>
                    {data?.predikatKinerjaRekomendasi.toLowerCase() ===
                      option.toLowerCase() && (
                      <Badge variant="informational">Rekomendasi</Badge>
                    )}
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter className="space-x-4">
            <Button
              color="white"
              size="lg"
              disabled={isMutating}
              onClick={onClose}
            >
              Kembali
            </Button>
            <Button
              color="blue"
              size="lg"
              disabled={isMutating}
              onClick={onSubmit}
            >
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DialogPredikatKinerjaTahunanUpdate
